import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import {
  Typography,
  Button,
  CircularProgress,
  DataTableV2,
  DataTableActionsV2,
  DataTableHeaderV2,
  DataTableBody,
  DataTableRowV2,
  DataTableCellV2,
  DataTableHeaderCellV2,
  DataTablePaginationV2,
  IconButton,
  Select,
  Option,
  SearchField,
  PopoverV2
} from '@getgo/chameleon-web-react-wrapper';
import {
  AddOutlinedIcon,
  EditOutlinedIcon,
  DoubleChevronLeftFilledIcon,
  DoubleChevronRightFilledIcon,
  ChevronLeftFilledIcon,
  ChevronRightIcon,
  PersonOutlinedIcon
} from '@getgo/chameleon-icons/react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import classes from '../../roles/components/role-list-view.scss';
import ResponsiveDialog from 'components/dialog-view.js';
import st from 'core/shared-translations';
import ScopeDetail from '../components/scope-detail';
import { flexRender, getCoreRowModel, getPaginationRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';

const t = defineMessages({
  'scope-management.title': {
    id: 'scope-management.title',
    defaultMessage: 'Scope management',
    description: 'title for the scope management',
  },
  'scope-management.createScopeCTA': {
    id: 'scope-management.createScopeCTA',
    defaultMessage: 'Add scope',
    description: 'Label for the add scope CTA',
  },
  'scope-management.editScope.title': {
    id: 'scope-management.editScope.title',
    defaultMessage: 'Edit scope',
    description: 'Label for the add scope CTA',
  },
  'scope-management.subtitle': {
    id: 'scope-management.subtitle',
    defaultMessage: 'Manage scopes',
    description: 'Subtitle for scope list view'
  },

});

const ScopesList = ({
  isloading,
  getApproversIsLoading,
  onClickAddRole,
  showAddRoleDialog,
  scopes,
  editOnClick,
  openEditRoleDialog,
  closeEditModel,
  onSubmit,
  onChange,
  scope,
  validateUserInput,
  errors,
  enableSubmit,
  autocompleteList,
  personIconClick,
  ownersList,
  popOverChange,
  autoCompleteRef,
  onClickAddOwner,
  onSort,
  sortDirection,
  meLocale,
}) => {
  const intl = useIntl();
  const [globalFilter, setGlobalFilter] = useState('');
  const columns = [
    { header: <FormattedMessage {...st['shared.role-management.table.column1']} />, filterFn: 'includesString', accessorKey: "id", cell: (props) => <Typography>{props.getValue()}</Typography>, enableSorting: true, },
    { header: <FormattedMessage {...st['shared.scope-management.table.column2']} />, filterFn: 'includesString', accessorKey: "groupId", cell: (props) => <Typography>{props.getValue()}</Typography>, enableSorting: true, },
    {
      header: <FormattedMessage {...st['shared.scope.managment.public']} />, accessorKey: "public", cell: (props) =>
        <Typography>{
          props.getValue() === true ?
            <FormattedMessage {...st['shared.role-management.active.yes']} /> :
            <FormattedMessage {...st['shared.role-management.active.no']} />
        }</Typography>
    },
    {
      header: <FormattedMessage {...st['shared.role.managment.active']} />, accessorKey: "active", cell: (props) =>
        <Typography>{
          props.getValue() === true ?
            <FormattedMessage {...st['shared.role-management.active.yes']} /> :
            <FormattedMessage {...st['shared.role-management.active.no']} />
        }
        </Typography>
    },
    {
      header: <FormattedMessage {...st['shared.role.managment.description']} />, accessorKey: "description", cell: (props) =>
        <Typography>{
          props.getValue() === undefined ? "" : props.getValue()[meLocale]
        }
        </Typography>
    }
  ];
  const table = useReactTable({
    data: scopes,
    columns,
    state: {
      globalFilter
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
  });

  const firstRowOnPage = table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1;
  const lastRowOnPage = Math.min(
    table.getState().pagination.pageIndex * table.getState().pagination.pageSize + table.getState().pagination.pageSize,
    table.getCoreRowModel().flatRows?.length,
  );
  const totalRows = table.getFilteredRowModel().flatRows.length;

  return (
    <Box>
      {isloading &&
        <CircularProgress id="loadingDiv" className={classes["loading"]} />}
      {!isloading &&
        <Box>
          <Box id="scope-list-section">
            <Box className={classes['listViewHeading']}>
              <Typography variant="heading-large">
                <FormattedMessage {...t['scope-management.title']} />
              </Typography>
              <Box className={classes['addRoleCTA']}>
                <Button
                  title={intl.formatMessage(t["scope-management.createScopeCTA"])}
                  className={classes["createRoleCta"]}
                  leadingIcon={<AddOutlinedIcon />}
                  onClick={onClickAddRole}
                >
                  <FormattedMessage {...t['scope-management.createScopeCTA']} />
                </Button>
              </Box>
            </Box>
            <Box className={classes["mainContentRoleView"]}>
              <Box className={classes["spaceBetweenContent"]}>
                <React.Fragment>
                  <Box className={classes["infoText"]}>
                    <Typography variant="heading-small">
                      <FormattedMessage {...t['scope-management.subtitle']} />
                    </Typography>
                  </Box>
                </React.Fragment>
              </Box>
              <Box className={classes["flexSideSmall"]}>
                <DataTableActionsV2
                  search={
                    <SearchField
                      labelId="Search by ID"
                      className={classes["searchIdField"]}
                      placeholder={intl.formatMessage(st["shared.role-management.search-feild.placeholder"])}
                      onChange={(e) => {
                        setGlobalFilter(e.target.value);
                      }}
                    />
                  }
                />
                <DataTableV2 className={classes["controlWidth"]}>
                  <DataTableHeaderV2 condensed>
                    {table.getHeaderGroups().map((headerGroup, index) =>
                      <DataTableRowV2 key={index} style={{ borderTop: "none" }}>
                        {headerGroup.headers.map((header, index) =>
                          <DataTableHeaderCellV2
                            key={`${header.column.columnDef.header}`}
                            {...(index === 0 && { sort: sortDirection, onSort: onSort })}
                          >
                            {header.column.columnDef.header}
                          </DataTableHeaderCellV2>
                        )}
                        <DataTableHeaderCellV2>
                          <FormattedMessage {...st[`shared.actions.title`]} />
                        </DataTableHeaderCellV2>
                      </DataTableRowV2>
                    )}
                  </DataTableHeaderV2>
                  <DataTableBody>
                    {table.getRowModel().rows.map((row) => (
                      <DataTableRowV2 key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <DataTableCellV2 key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </DataTableCellV2>))}
                        {row.getVisibleCells().map((cell) => {
                          if (cell.column.id === 'id') {
                            return (
                              <DataTableCellV2 key={row.id}>
                                <IconButton
                                  labelId="person-icon"
                                  data-testid={`person${row.id}`}
                                  id={`trigger${row.id}`}
                                  onClick={() => personIconClick(cell.getValue())}>
                                  <PersonOutlinedIcon color="#2C72DE" />
                                </IconButton>
                                <IconButton
                                  labelId="edit-icon"
                                  data-testid={`trigger${row.id}`}
                                  id={`trigger${row.id}`}
                                  onClick={() => editOnClick(cell.getValue())}>
                                  <EditOutlinedIcon color="#2C72DE" />
                                </IconButton>
                                <PopoverV2
                                  id="owner_popover"
                                  menu
                                  onChange={popOverChange}
                                  label="Popover label"
                                  triggerId={`trigger${row.id}`}>
                                  {!getApproversIsLoading && <Box style={{ maxWidth: '250px', padding: '8px', width: "auto" }}>
                                    {(ownersList && ownersList.length) ?
                                      <Box>
                                        <Typography style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                          <FormattedMessage
                                            {...st['shared.role-management.role.approvers']}
                                            values={{
                                              b: (chunks) => <b>{chunks}</b>,
                                              roleId: cell.getValue()
                                            }} />
                                        </Typography>
                                        {ownersList.map((value, index) => (
                                          <React.Fragment key={index}>
                                            <Typography style={{ textOverflow: 'ellipsis', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                              {value}
                                            </Typography>
                                          </React.Fragment>))}
                                      </Box> :
                                      <Typography>
                                        <FormattedMessage {...st['shared.role-management.role.no.approvers']} />
                                      </Typography>}
                                  </Box>}
                                </PopoverV2>
                              </DataTableCellV2>
                            );
                          }
                          return null;
                        })}
                      </DataTableRowV2>
                    ))}
                  </DataTableBody>
                </DataTableV2>
                <DataTablePaginationV2
                  select={
                    <Box className={classes['pageSizeView']}>
                      <Typography>
                        <FormattedMessage {...st['shared.row-per-page']} />
                      </Typography>
                      <Select
                        data-testid="page-size-select"
                        value={table.getState().pagination.pageSize.toString()}
                        onChange={(e) => {
                          table.setPageSize(Number(e.target.value));
                        }}
                      >
                        {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                          <Option key={pageSize} value={pageSize}>
                            {pageSize}
                          </Option>
                        ))}
                      </Select>
                    </Box>
                  }
                  counterText={`${firstRowOnPage} - ${lastRowOnPage} of ${totalRows}`}
                  actions={
                    <Box>
                      <IconButton
                        data-testid="first-page"
                        labelId="Go to first page"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                      >
                        <DoubleChevronLeftFilledIcon />
                      </IconButton>
                      <IconButton
                        data-testid="previous-page"
                        labelId="Go to previous page"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                      >
                        <ChevronLeftFilledIcon />
                      </IconButton>
                      <Typography variant="caption-medium-strong" color="type-color-secondary">
                        <FormattedMessage {...st["shared.pagination.page"]} /> {table.getState().pagination.pageIndex + 1}/{table.getPageCount()}
                      </Typography>
                      <IconButton data-testid="next-page" labelId="Go to next page" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                        <ChevronRightIcon />
                      </IconButton>
                      <IconButton
                        data-testid="last-page"
                        labelId="Go to last page"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                      >
                        <DoubleChevronRightFilledIcon />
                      </IconButton>
                    </Box>
                  }
                />
              </Box>
            </Box>
          </Box>
          <ResponsiveDialog
            title={openEditRoleDialog ?
              <FormattedMessage {...t["scope-management.editScope.title"]} /> :
              <FormattedMessage {...t["scope-management.createScopeCTA"]} />}
            open={openEditRoleDialog || showAddRoleDialog}
            closable
            size="large"
            onClosed={closeEditModel}
            actions={
              <React.Fragment>
                <Button
                  data-testid="per-saveBtn"
                  disabled={!enableSubmit}
                  onClick={() => {
                    onSubmit();
                    setGlobalFilter('');
                  }}
                >
                  <FormattedMessage {...st['shared.save']} />
                </Button>
                <Button
                  data-testid="per-canBtn"
                  onClick={() => {
                    closeEditModel();
                    setGlobalFilter('');
                  }}
                  variant="tertiary">
                  <FormattedMessage {...st["shared.cancel"]} />
                </Button>
              </React.Fragment>
            }
          >
            <ScopeDetail
              isEditMode={openEditRoleDialog}
              scope={scope}
              onChange={onChange}
              onBlur={validateUserInput}
              errors={errors}
              autocompleteList={autocompleteList || []}
              ownersList={ownersList}
              autoCompleteRef={autoCompleteRef}
              onClickAddOwner={onClickAddOwner}
            />
          </ResponsiveDialog>
        </Box>}
    </Box>);
};
ScopesList.propTypes = {
  isloading: PropTypes.bool.isRequired,
  getApproversIsLoading: PropTypes.bool.isRequired,
  onClickAddRole: PropTypes.func.isRequired,
  showAddRoleDialog: PropTypes.bool.isRequired,
  scopes: PropTypes.array.isRequired,
  editOnClick: PropTypes.func.isRequired,
  openEditRoleDialog: PropTypes.bool.isRequired,
  closeEditModel: PropTypes.func.isRequired,
  getValue: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  scope: PropTypes.object.isRequired,
  validateUserInput: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  enableSubmit: PropTypes.bool.isRequired,
  autocompleteList: PropTypes.array,
  personIconClick: PropTypes.func.isRequired,
  ownersList: PropTypes.array.isRequired,
  personIconClicked: PropTypes.bool.isRequired,
  popOverChange: PropTypes.func.isRequired,
  autoCompleteRef: PropTypes.object.isRequired,
  onClickAddOwner: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  sortDirection: PropTypes.string.isRequired,
  meLocale: PropTypes.string.isRequired
};

export default ScopesList;
